import { useDSToast } from '@dealstack/frontend/shared/mechanism/toast/useDSToast';
import { useCallback, useState } from 'react';
import { saveAs } from 'file-saver';
import { asyncLoadExceljs } from '@dealstack/frontend/shared/utils/asyncLoadExceljs';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import {
	bulkCreateNewTSAUploadFile,
	getMipInvestmentsData,
} from '@dealstack/frontend/shared/dataExchange/services/god.service';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { EXCEL_SPREADSHEET_TYPE } from '@dealstack/shared/lib/common/business/excel';
import {
	AlertDescription,
	AlertTitle,
	Alert,
} from '@dealstack/frontend/shared/components/Alert/Alert';

export const useCreateInvestmentController = (mipId: string) => {
	const getMipInvestmentsDataRequest = useQuery({
		queryKey: [formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_INVESTMENTS_GET, { mipId })],
		queryFn: ({ signal }) =>
			getMipInvestmentsData(
				formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_INVESTMENTS_GET, { mipId }),
				signal
			),
		meta: { suppressErrorToast: true },
	});

	const toastApi = useDSToast();

	const [showBulkCreateModal, setShowBulkCreateModal] = useState(false);
	const [skippedReasons, setSkippedReasons] = useState<string[] | null>(null);

	const onCloseModal = useCallback(() => setShowBulkCreateModal(false), []);

	const onOpenModal = useCallback(() => setShowBulkCreateModal(true), []);
	const onDownloadXLSXTemplate = async (): Promise<void> => {
		const ExcelJS = await asyncLoadExceljs();
		const workbook = new ExcelJS.Workbook();
		workbook.created = new Date();

		const dataWorksheet = workbook.addWorksheet(`bulk-create-investments`);

		const columnsName = [
			'investorId',
			'transaction date',
			'security name',
			'security type',
			'count',
			'cost',
		];

		const columnWidths = {
			investorId: 40,
			'transaction date': 20,
			'security name': 20,
			'security type': 20,
			count: 10,
			cost: 10,
		};

		dataWorksheet.columns = columnsName.map((name) => ({
			header: name,
			key: name,
			width: columnWidths[name] || 20,
		}));

		dataWorksheet.addRows([
			{
				investorId: '<investorId>',
				'transaction date': '<dd.mm.yyyy>',
				'security name': '<security name>',
				'security type': '<security type>',
				count: '<count>',
				cost: '<cost>',
			},
		]);

		const xls64 = await workbook.xlsx.writeBuffer();

		saveAs(
			new Blob([xls64], {
				type: EXCEL_SPREADSHEET_TYPE,
			}),
			`create-share-association-mip--${mipId}.xlsx`
		);
	};

	const bulkCreateUpload = useWrappedRequest<
		{ formData: FormData; onUploadProgress?: (percent: number) => void },
		ApiV1.Response<string[]>
	>(
		bulkCreateNewTSAUploadFile(mipId),
		{},
		{
			onSuccessDefault: ({ payload }) => {
				// oxlint-disable-next-line typescript/no-floating-promises
				getMipInvestmentsDataRequest.refetch();
				setShowBulkCreateModal(false);
				if (Array.isArray(payload) && payload.length > 0) {
					setSkippedReasons(payload);
					toastApi.success({
						title: 'Bulk create completed',
						message: 'Some rows were skipped',
					});
				} else {
					setSkippedReasons(null);
					toastApi.success({ title: 'Bulk create completed' });
				}
			},
			onLocalErrorDefault: ({ error }) => {
				const message = error?.code ? error.message : 'Error during uploading file';
				toastApi.error({ message });
				logger.error(`Investments create ${error}`);
			},
		}
	);

	const renderBulkCreateAlert = () => {
		if (!skippedReasons) {
			return null;
		}
		return (
			<Alert size="m" state="danger" closeButton onClose={() => setSkippedReasons(null)}>
				<AlertTitle>Some rows were skipped</AlertTitle>
				<AlertDescription>
					<ul style={{ margin: 0, paddingLeft: '1rem' }}>
						{skippedReasons.map((reason, idx) => (
							<li key={idx}>{reason}</li>
						))}
					</ul>
				</AlertDescription>
			</Alert>
		);
	};

	return {
		showBulkCreateModal,
		setShowBulkCreateModal,
		onDownloadXLSXTemplate,
		onOpenModal,
		onCloseModal,
		bulkCreateUpload,
		renderBulkCreateAlert,
		clearBulkCreateAlert: () => setSkippedReasons(null),
	};
};
